import { useCallback, useMemo } from 'react'

import { useApolloClient } from '@apollo/client'
import type { InFilterDefinition } from '@factorialco/f0-react'
import { EmployeeWorkplacesFilterDocument } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

const useWorkplaceBaseFilter = ({
  includeWorkAreas,
}: {
  includeWorkAreas: boolean
}): InFilterDefinition<string> => {
  const client = useApolloClient()

  const loadOptions = useCallback(async () => {
    const { data } = await client.query({
      query: EmployeeWorkplacesFilterDocument,
      variables: {
        includeWorkAreas,
      },
      fetchPolicy: 'cache-first',
    })

    return data.locations.locations.map((location) => {
      const workAreas = location.workAreas ?? []

      const children =
        includeWorkAreas && workAreas.length > 0
          ? {
              filterKey: 'workArea',
              options: workAreas.map((workArea) => ({
                label: workArea.name,
                value: workArea.id,
              })),
            }
          : undefined

      return {
        label: location.name,
        value: location.id,
        ...(children ? { children } : {}),
      }
    })
  }, [client, includeWorkAreas])

  return useMemo(
    () => ({
      label: i18n.t('employees.employees_list.headers.workplace'),
      type: 'in' as const,
      options: {
        cache: true,
        options: loadOptions,
      },
    }),
    [loadOptions]
  )
}

export const useWorkplaceFilter = ({
  withWorkAreas = false,
}: {
  withWorkAreas?: boolean
} = {}): InFilterDefinition<string> => {
  return useWorkplaceBaseFilter({ includeWorkAreas: withWorkAreas })
}
